import React from 'react';
import {Box, Skeleton} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import type {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';
import {formatStatValue} from '@/views/campaigns/stats/utils';
import styles from './StatCard.module.scss';

interface StatCardProps {
  testID?: string;
  iconName: IconProps['name'];
  label: string;
  // Accepts either a raw number (formatted internally via `format`) or a
  // pre-formatted string (rendered as-is). Pre-formatted strings are useful
  // when callers already have display-ready values (e.g. "6.1k").
  value: number | string | undefined;
  format?: 'number' | 'percentage';
  isLoading?: boolean;
  highlighted?: boolean;
}

function StatCard({
  testID,
  iconName,
  label,
  value,
  format = 'number',
  isLoading = false,
  highlighted = false,
}: StatCardProps) {
  const cardClassName = `${styles.statCard}${highlighted ? ` ${styles.highlighted}` : ''}`;

  if (isLoading) {
    return (
      <Box className={cardClassName} data-testid={`${testID}-loading`}>
        <Skeleton variant="text" className={styles.skeletonValue} />
        <Skeleton variant="text" className={styles.skeletonLabel} />
      </Box>
    );
  }

  // The Figma "Empty" variant fades out the value and the icon when there is
  // no data for the stat, so a missing metric reads as visually subdued
  // rather than just an em dash in the regular text color.
  const isEmpty = value === undefined || value === null || value === '';
  const valueColor = isEmpty
    ? designSystemToken('semantic.fg.disabled')
    : designSystemToken('semantic.fg.primary');

  let displayValue: string;
  if (isEmpty) {
    displayValue = '—';
  } else if (typeof value === 'string') {
    displayValue = value;
  } else {
    displayValue = formatStatValue(value, format);
  }

  return (
    <Box className={cardClassName} data-testid={testID}>
      <Box className={styles.statRow}>
        <Box className={styles.iconWrapper}>
          <Icon
            size="small"
            appearance={isEmpty ? 'disabled' : 'neutral'}
            name={iconName}
          />
        </Box>
        <Box className={styles.statContent}>
          <Label size="lg" color={valueColor}>
            {displayValue}
          </Label>
          <Body
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
            className={styles.statLabel}
          >
            {label}
          </Body>
        </Box>
      </Box>
    </Box>
  );
}

export default StatCard;
